import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { Hub } from 'aws-amplify/utils';
import { AuthService, AuthState } from '../services/auth';
import { MockAuthService } from '../services/mockAuth';
import { isDemoMode } from '../services/mockData';

// Check if SSO is enabled via environment variable
const isSSOEnabled = (): boolean => {
  const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
  const result = !!cognitoDomain && cognitoDomain.length > 0;
  
  console.log('[isSSOEnabled] SSO configuration check:', {
    cognitoDomain,
    result,
    REACT_APP_COGNITO_DOMAIN: process.env.REACT_APP_COGNITO_DOMAIN
  });
  
  return result;
};

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<{ needsNewPassword?: boolean }>;
  signInWithAzureAD: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  isDemoMode: boolean;
  needsNewPassword: boolean;
  isSSOEnabled: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider for managing authentication state
 * Supports both real AWS Cognito and demo mode
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true
  });
  const [needsNewPassword, setNeedsNewPassword] = useState(false);

  const demoMode = isDemoMode();
  const authService = useMemo(
    () => (demoMode ? new MockAuthService() : new AuthService()),
    [demoMode]
  );
  const ssoEnabled = isSSOEnabled(); // Allow SSO even in demo mode

  const checkAuthState = useCallback(async () => {
    try {
      const user = await authService.getCurrentUser();
      setAuthState({
        user,
        isAuthenticated: !!user,
        isLoading: false
      });
    } catch (error) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
    }
  }, [authService]);

  useEffect(() => {
    // Check if this is an OAuth callback (URL has code parameter)
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = urlParams.has('code');
    const hasError = urlParams.has('error');
    
    if (hasCode) {
      console.log('[Auth] OAuth callback detected with code');
      // Amplify will automatically exchange the code for tokens
      // Just wait a moment then check auth state
      setTimeout(() => {
        checkAuthState();
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 1000);
    } else if (hasError) {
      console.error('[Auth] OAuth error:', urlParams.get('error'), urlParams.get('error_description'));
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
    } else {
      checkAuthState();
    }
    
    // Listen for auth events (for SSO redirect handling)
    const hubListener = Hub.listen('auth', ({ payload }) => {
      console.log('[Auth Hub] Event:', payload.event, payload);
      switch (payload.event) {
        case 'signedIn':
          console.log('[Auth Hub] User signed in');
          checkAuthState();
          break;
        case 'signedOut':
          console.log('[Auth Hub] User signed out');
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false
          });
          break;
        case 'tokenRefresh':
          console.log('[Auth Hub] Token refreshed');
          break;
        case 'tokenRefresh_failure':
          console.log('[Auth Hub] Token refresh failed');
          break;
        case 'signInWithRedirect':
          console.log('[Auth Hub] Sign in with redirect started');
          break;
        case 'signInWithRedirect_failure':
          console.log('[Auth Hub] Sign in with redirect failed', payload);
          break;
        case 'customOAuthState':
          console.log('[Auth Hub] Custom OAuth state');
          break;
      }
    });

    return () => {
      hubListener();
    };
  }, [checkAuthState]);

  const signIn = async (email: string, password: string): Promise<{ needsNewPassword?: boolean }> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      const result = await authService.signIn(email, password);
      
      // Check if new password is required (only for real Cognito auth)
      const resultAny = result as any;
      if (resultAny.nextStep === 'NEW_PASSWORD_REQUIRED' || resultAny.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return { needsNewPassword: true };
      }
      
      if (result.isSignedIn) {
        const user = await authService.getCurrentUser();
        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false
        });
        setNeedsNewPassword(false);
        return { needsNewPassword: false };
      }
      
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return { needsNewPassword: false };
    } catch (error) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
      throw error;
    }
  };

  const completeNewPassword = async (newPassword: string): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      const result = await (authService as AuthService).completeNewPassword(newPassword);
      
      if (result.isSignedIn) {
        const user = await authService.getCurrentUser();
        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false
        });
        setNeedsNewPassword(false);
      }
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await authService.signOut();
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
      setNeedsNewPassword(false);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    return authService.resetPassword(email);
  };

  const confirmResetPassword = async (email: string, code: string, newPassword: string): Promise<void> => {
    return authService.confirmResetPassword(email, code, newPassword);
  };

  const signInWithAzureAD = async (): Promise<void> => {
    if (demoMode) {
      throw new Error('SSO is not available in demo mode');
    }
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      await (authService as AuthService).signInWithAzureAD();
      // The redirect will happen, and Hub listener will handle the callback
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const value: AuthContextType = {
    ...authState,
    signIn,
    signInWithAzureAD,
    signOut,
    resetPassword,
    confirmResetPassword,
    completeNewPassword,
    isDemoMode: demoMode,
    needsNewPassword,
    isSSOEnabled: ssoEnabled
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
